import { DocumentType } from '@/services/DocumentsService.interface'
import Cookies from 'js-cookie'
import { CookieName } from '@/config/cookies'
import YoonlyService from '@/services/YoonlyService'

/** Insurance PDFs for www.befr.yoonly.eu / befr.yoonly.eu (hosted on www.yoonly.eu). */
const YOONLY_BEFR_INSURANCE = {
  annulation: 'https://www.yoonly.eu/content/uploads/AnnulYN_FR.pdf',
  skieur: 'https://www.yoonly.eu/content/uploads/SkieurYN_FR.pdf',
} as const

const isYoonlyBefrDomain = (host: string): boolean => {
  const h = host.toLowerCase()
  return h === 'www.befr.yoonly.eu' || h === 'befr.yoonly.eu'
}

// Configuration for insurance document links based on documentType and locale
export interface InsuranceDocumentConfig {
  [documentType: string]: {
    [locale: string]: string
  }
}

export const insuranceDocumentLinks: InsuranceDocumentConfig = {
  [DocumentType.InsuranceSkieur]: {
    'fr-FR': 'https://www.travelski.com/Content/SkieurTS_FR_2025.pdf',
    'en-GB': 'https://www.travelski.com/Content/SkieurTS_UK_2025.pdf', 
    'en-US': 'https://www.travelski.com/Content/SkieurTS_UK_2025.pdf',
    'nl-NL': 'https://www.travelski.com/Content/SkieurTS_NL_2025.pdf',
    'fr-FR-CSE': 'https://www.travelski.com/Content/SkieurTS_FR_2025.pdf',
  },
  [DocumentType.InsuranceAnnulation]: {
    'fr-FR': 'https://www.travelski.com/Content/ZenTS_FR_2025.pdf',
    'en-GB': 'https://www.travelski.com/Content/ZenTS_UK_2025.pdf',
    'en-US': 'https://www.travelski.com/Content/ZenTS_UK_2025.pdf', 
    'nl-NL': 'https://www.travelski.com/Content/ZenTS_NL_2025.pdf',
    'fr-FR-CSE': 'https://www.travelski.com/Content/ZenTS_FR_2025.pdf',
    'fr-FR-CSE-COSOG': 'https://www.travelski.com/Content/Annul_COSOG_FR.pdf',
  }
}

/**
 * Add timestamp to URL for cache busting
 * @param url - The base URL
 * @returns URL with timestamp parameter
 */
const addTimestamp = (url: string): string => {
  const timestamp = Date.now()
  const separator = url.includes('?') ? '&' : '?'
  return `${url}${separator}t=${timestamp}`
}

/**
 * Get the file link for an insurance document based on type and locale
 * @param documentType - The type of insurance document
 * @param locale - The current locale (e.g., 'fr-FR', 'en-US', 'nl-NL', 'fr-FR-CSE')
 * @returns The file link URL or empty string if not found
 * 
 * Special CSE Logic:
 * - For 'fr-FR-CSE' locale, checks the 'companyLang' cookie
 * - If companyLang = 'nl' → returns Dutch (nl-NL) file
 * - If companyLang = 'fr' (or missing) → returns French (fr-FR) file
 */
export const getInsuranceDocumentLink = (documentType: DocumentType, locale: string): string => {
  const yoonly = new YoonlyService()
  if (yoonly.isYoonly && isYoonlyBefrDomain(yoonly.getDomain())) {
    if (documentType === DocumentType.InsuranceSkieur) {
      return addTimestamp(YOONLY_BEFR_INSURANCE.skieur)
    }
    if (documentType === DocumentType.InsuranceAnnulation) {
      return addTimestamp(YOONLY_BEFR_INSURANCE.annulation)
    }
  }

  const documentConfig = insuranceDocumentLinks[documentType]
  if (!documentConfig) {
    return ''
  }
  
  let baseUrl = ''
  
  // Special handling for CSE: check companyLang cookie
  if (locale === 'fr-FR-CSE') {
    const companyLang = Cookies.get(CookieName.COMPANY_LANG)
    
    if (companyLang === 'nl') {
      // For CSE with Dutch company language, return Dutch file
      baseUrl = documentConfig['nl-NL'] || documentConfig['fr-FR'] || ''
    } else {
      // For CSE with French company language (or fallback), return French file
      baseUrl = documentConfig['fr-FR'] || ''
    }
  } else {
    // Return the link for the specific locale, fallback to fr-FR if not found
    baseUrl = documentConfig[locale] || documentConfig['fr-FR'] || ''
  }
  
  // Add timestamp for cache busting
  return baseUrl ? addTimestamp(baseUrl) : ''
}

/**
 * Check if a document type is an insurance document
 * @param documentType - The document type to check
 * @returns True if it's an insurance document type
 */
export const isInsuranceDocument = (documentType: DocumentType): boolean => {
  return documentType === DocumentType.InsuranceSkieur || documentType === DocumentType.InsuranceAnnulation
}
